import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Eye, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Spinner, EmptyState, PageHeader, StatusBadge, FormatBadge } from "@/components/common";
import { api, media, naira, errMsg } from "@/lib/api";

function EditDialog({ t, onClose, onSaved }) {
  const [f, setF] = useState({ title: t.title, description: t.description, price: t.price, dimensions: t.dimensions, tags: t.tags.join(", ") });
  const save = async () => {
    try {
      await api.put(`/seller/templates/${t.id}`, { ...f, price: Number(f.price), tags: f.tags.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Listing updated"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit listing</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input data-testid="edit-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="edit-description-input" rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Price (₦)</Label><Input data-testid="edit-price-input" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Dimensions</Label><Input data-testid="edit-dimensions-input" value={f.dimensions} onChange={(e) => setF({ ...f, dimensions: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Tags</Label><Input data-testid="edit-tags-input" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></div>
          <Button className="w-full" onClick={save} data-testid="edit-save-btn">Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SellerTemplates() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/seller/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.delete(`/seller/templates/${id}`); toast.success("Listing removed"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="Catalog" title="My Templates">
        <Button asChild className="rounded-full" data-testid="templates-upload-btn"><Link to="/seller/upload"><Plus size={16} className="mr-1" />Upload</Link></Button>
      </PageHeader>
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No templates yet" text="Upload your first design to start selling." testid="seller-templates-empty" /> : (
        <div className="grid gap-3">
          {items.map((t) => (
            <div key={t.id} className="card-surface flex flex-col gap-4 p-4 sm:flex-row sm:items-center" data-testid={`seller-template-${t.id}`}>
              <img src={media(t.cover_image)} alt="" className="h-20 w-16 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><p className="truncate font-semibold">{t.title}</p><StatusBadge s={t.status} testid={`seller-template-status-${t.id}`} /></div>
                <p className="mt-1 text-xs text-muted-foreground">{t.category_name} · {naira(t.price)} · {t.sales_count} sales · {t.downloads_count} downloads</p>
                <div className="mt-1.5 flex gap-1">{t.file_formats.map((f) => <FormatBadge key={f} f={f} />)}</div>
                {t.rejection_reason && <p className="mt-1.5 text-xs text-rose-500">Reason: {t.rejection_reason}</p>}
              </div>
              <div className="flex gap-2">
                <Button asChild size="icon" variant="ghost"><Link to={`/template/${t.id}`} data-testid={`view-template-${t.id}`}><Eye size={16} /></Link></Button>
                <Button size="icon" variant="ghost" onClick={() => setEditing(t)} data-testid={`edit-template-${t.id}`}><Pencil size={16} /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-rose-500" data-testid={`delete-template-${t.id}`}><Trash2 size={16} /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove this listing?</AlertDialogTitle><AlertDialogDescription>It will be hidden from the marketplace. Existing buyers keep their downloads.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t.id)} data-testid="confirm-delete-btn">Remove</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <EditDialog t={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
